import type { AuditFilter, AuditLogResponse } from "../../shared/audit";
import type {
  AddNoteResponse, CreateReportResponse, ManualEntryRequest, ManualEntryResponse, DashboardResponse, ReportsResponse, TraceResponse, FindingDetailResponse, FindingStatus, FindingsResponse, FindingsSort, FindingRule, Severity, RunItemsResponse, RunResponse,
  IpaDetailResponse, IpaListResponse, IntegrityResponse, RecordHistoryResponse, UploadChangesResponse, PreviewResponse, ProfileResponse, SaveProfileRequest, SaveProfileResponse, SourcesResponse, UploadDetailResponse, UploadMapping, UploadRecordsResponse, UploadResponse,
} from "../../shared/api";
import type {
  AccountResponse, ChangePasswordRequest, CreateUserRequest, CreateUserResponse, ErrorBody, HealthResponse, LoginRequest, PasswordResetResponse,
  PatchUserRequest, SessionResponse, SetPasswordRequest, SettingsResponse, UserItem, UsersResponse,
} from "../../shared/api";

/** Non-2xx API answer. `body` is the ErrorBody (ADR-0005). */
export class ApiFailure extends Error {
  constructor(public status: number, public body: ErrorBody) {
    super(body.message);
  }
}

// App listens: a 401 anywhere means the session is gone, MUST_SET_PASSWORD means it is a temporary one.
let onSessionLost: (code: "UNAUTHENTICATED" | "MUST_SET_PASSWORD") => void = () => {};
export const setSessionLostHandler = (fn: typeof onSessionLost) => { onSessionLost = fn; };

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.ok) return (res.status === 204 ? undefined : await res.json()) as T;
  const err: ErrorBody = await res.json().catch(() => ({
    code: "INTERNAL_ERROR", message: "Beklenmeyen bir hata oluştu.", requestId: res.headers.get("x-request-id") ?? "-",
  }));
  if (err.code === "UNAUTHENTICATED" || err.code === "MUST_SET_PASSWORD") onSessionLost(err.code);
  throw new ApiFailure(res.status, err);
}

/** Signed-out safe: company name, version and build for the sign-in screen, sidebar and footer. A failure only hides them. */
export const getHealth = () => request<HealthResponse>("GET", "/api/health").then((h) => h, () => null);
export const getSession = () => request<SessionResponse>("GET", "/api/session");
export const login = (b: LoginRequest) => request<SessionResponse>("POST", "/api/session", b);
export const logout = () => request<void>("DELETE", "/api/session");
export const setPassword = (b: SetPasswordRequest) => request<void>("POST", "/api/session/password", b);

// ---- Hesabım, E9, E10 (ADR-0017). tempPassword yalnız çağıranın bileşen durumuna girer; burada saklanmaz. ----
export const getAccount = () => request<AccountResponse>("GET", "/api/account");
export const changePassword = (b: ChangePasswordRequest) => request<void>("POST", "/api/session/password", b);
export const getUsers = () => request<UsersResponse>("GET", "/api/users");
export const createUser = (b: CreateUserRequest) => request<CreateUserResponse>("POST", "/api/users", b);
export const patchUser = (username: string, b: PatchUserRequest) => request<UserItem>("PATCH", `/api/users/${encodeURIComponent(username)}`, b);
export const resetUserPassword = (username: string) => request<PasswordResetResponse>("POST", `/api/users/${encodeURIComponent(username)}/password-reset`);
export const getSettings = () => request<SettingsResponse>("GET", "/api/settings");

/** 400 `fields` → {alan: ileti}; alana bağlanamayan hatada null (ErrorAlert gösterilir). */
export function fieldErrors(e: unknown): Record<string, string> | null {
  if (!(e instanceof ApiFailure) || !Array.isArray(e.body.fields)) return null;
  const out: Record<string, string> = {};
  for (const f of e.body.fields as { field: string; message: string }[]) out[f.field] ??= f.message;
  return Object.keys(out).length ? out : null;
}

/** Query string of GET /api/audit-log and its xlsx twin; empty filter fields are left out. */
export function auditQuery(f: AuditFilter, page?: number, pageSize?: number): string {
  const q = new URLSearchParams();
  for (const k of ["user", "eventType", "from", "to"] as const) if (f[k]?.trim()) q.set(k, f[k]!.trim());
  if (page) q.set("page", String(page));
  if (pageSize) q.set("pageSize", String(pageSize));
  const s = q.toString();
  return s ? `?${s}` : "";
}
export const getAuditLog = (f: AuditFilter, page: number, pageSize: number) =>
  request<AuditLogResponse>("GET", `/api/audit-log${auditQuery(f, page, pageSize)}`);

/** Name from Content-Disposition; else `<base>-<Istanbul day>.xlsx` (design: denetim-logu.md). */
export function xlsxFileName(disposition: string | null, base: string, now = new Date(), ext = "xlsx"): string {
  const m = disposition?.match(/filename\*=UTF-8''([^;]+)/i) ?? disposition?.match(/filename="?([^";]+)"?/i);
  if (m) try { return decodeURIComponent(m[1]!); } catch { /* fall through */ }
  return `${base}-${now.toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" })}.${ext}`;
}

/** xlsx download: bytes + file name. Non-2xx arrives as an ApiFailure like any other error; `base` is the fallback name. */
async function xlsxDownload(url: string, base: string, ext = "xlsx"): Promise<{ blob: Blob; fileName: string }> {
  const res = await fetch(url);
  if (res.ok) return { blob: await res.blob(), fileName: xlsxFileName(res.headers.get("content-disposition"), base, new Date(), ext) };
  const body: ErrorBody = await res.json().catch(() => ({
    code: "INTERNAL_ERROR", message: "Beklenmeyen bir hata oluştu.", requestId: res.headers.get("x-request-id") ?? "-",
  }));
  if (body.code === "UNAUTHENTICATED" || body.code === "MUST_SET_PASSWORD") onSessionLost(body.code);
  throw new ApiFailure(res.status, body);
}

/** xlsx bytes of the filtered log. 422 EXPORT_TOO_LARGE arrives as an ApiFailure like any other error. */
export const getAuditXlsx = (f: AuditFilter) => xlsxDownload(`/api/audit-log/xlsx${auditQuery(f)}`, "denetim-logu");

// ---- E2 / E2b (ADR-0005) ----
export const getHistory = (sourceType: string, recordKey: string) =>
  request<RecordHistoryResponse>("GET", `/api/records/history?${new URLSearchParams({ sourceType, recordKey })}`);
export const getUploadChanges = (id: number) => request<UploadChangesResponse>("GET", `/api/uploads/${id}/changes`);
export const getSources = () => request<SourcesResponse>("GET", "/api/sources");
export const getIntegrity = () => request<IntegrityResponse>("GET", "/api/integrity");
export const getProfiles = (sourceType?: string) => request<ProfileResponse[]>("GET", `/api/profiles${sourceType ? `?sourceType=${sourceType}` : ""}`);
export const getUpload = (id: number) => request<UploadDetailResponse>("GET", `/api/uploads/${id}`);
export function getUploadRecords(id: number, o: { page?: number; keyContains?: string } = {}) {
  const q = new URLSearchParams({ page: String(o.page ?? 1) });
  if (o.keyContains) q.set("keyContains", o.keyContains);
  return request<UploadRecordsResponse>("GET", `/api/uploads/${id}/records?${q}`);
}
export const saveProfile = (b: SaveProfileRequest) => request<SaveProfileResponse>("POST", "/api/profiles", b);

/** multipart gövdesi: `file`, isteğe bağlı `sheet`, `mapping` (JSON), `period` (JSON, ADR-0018), `justification`. Content-Type'ı tarayıcı koyar. */
export type UploadOptions = { sheet?: string; mapping?: UploadMapping; justification?: string; period?: { start: string; end: string } };
export function uploadForm(file: File, o: UploadOptions): FormData {
  const f = new FormData();
  f.set("file", file);
  if (o.sheet) f.set("sheet", o.sheet);
  if (o.mapping) f.set("mapping", JSON.stringify(o.mapping));
  if (o.period) f.set("period", JSON.stringify(o.period));
  if (o.justification?.trim()) f.set("justification", o.justification.trim());
  return f;
}
async function sendForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method: "POST", body: form });
  if (res.ok) return (await res.json()) as T;
  const err: ErrorBody = await res.json().catch(() => ({
    code: "INTERNAL_ERROR", message: "Beklenmeyen bir hata oluştu.", requestId: res.headers.get("x-request-id") ?? "-",
  }));
  if (err.code === "UNAUTHENTICATED" || err.code === "MUST_SET_PASSWORD") onSessionLost(err.code);
  throw new ApiFailure(res.status, err);
}
/** Durumsuz önizleme: hiçbir şey saklanmaz. */
export const previewUpload = (file: File, o: UploadOptions) =>
  sendForm<PreviewResponse>("/api/uploads/preview", uploadForm(file, o));
export const createUpload = (file: File, o: UploadOptions) =>
  sendForm<UploadResponse>("/api/uploads", uploadForm(file, o));

/** E2c (ADR-0016). 201 carries the new version and the key of the added or corrected record. */
export const createManualEntry = (b: ManualEntryRequest) => request<ManualEntryResponse>("POST", "/api/manual-entries", b);

// ---- E3 (ADR-0005) ----
export const runReconciliation = () => request<RunResponse>("POST", "/api/runs");
/** İlk koşudan önce 404 NOT_FOUND: null döner. */
/** Son koşu ve koşu kilidi (spec: GET /api/runs/latest). 404 gövdesi de `running` taşır. */
export const getRunState = (): Promise<{ run: RunResponse | null; running: boolean }> =>
  request<RunResponse>("GET", "/api/runs/latest").then((run) => ({ run, running: run.running === true })).catch((e) => {
    if (e instanceof ApiFailure && e.status === 404) return { run: null, running: (e.body as { running?: boolean }).running === true };
    throw e;
  });
export const getLatestRun = () => getRunState().then((s) => s.run);
export type FindingsQuery = { status?: FindingStatus; severity?: Severity[]; rule?: FindingRule[]; q?: string; sort?: FindingsSort; page?: number; pageSize?: number };
export const getFindings = (status: FindingStatus, page = 1, pageSize = 50) => searchFindings({ status, page, pageSize });
export const searchFindings = (f: FindingsQuery = {}) => {
  const p = new URLSearchParams();
  if (f.status) p.set("status", f.status);
  f.severity?.forEach((v) => p.append("severity", v));
  f.rule?.forEach((v) => p.append("rule", v));
  if (f.q?.trim()) p.set("q", f.q.trim());
  if (f.sort) p.set("sort", f.sort);
  p.set("page", String(f.page ?? 1));
  p.set("pageSize", String(f.pageSize ?? 50));
  return request<FindingsResponse>("GET", `/api/findings?${p}`);
};
export const getRunItems = (q = "", page = 1, pageSize = 50) =>
  request<RunItemsResponse>("GET", `/api/runs/latest/items?${new URLSearchParams({ q, page: String(page), pageSize: String(pageSize) })}`);
export const getFinding = (id: number) => request<FindingDetailResponse>("GET", `/api/findings/${id}`);
export const addNote = (id: number, body: string) => request<AddNoteResponse>("POST", `/api/findings/${id}/notes`, { body });

// ---- E5 (A7) ----
export const getIpaList = () => request<IpaListResponse>("GET", "/api/ipa");
export const getIpaDetail = (ipaNo: string) => request<IpaDetailResponse>("GET", `/api/ipa/detail?${new URLSearchParams({ ipaNo })}`);
export const getIpaXlsx = (ipaNo: string) =>
  xlsxDownload(`/api/ipa/detail/xlsx?${new URLSearchParams({ ipaNo })}`, "gerceklesme-dokumu");

// ---- E4 (ADR-0005) ----
export const getTracePdf = (ref: string) => xlsxDownload(`/api/trace/pdf?${new URLSearchParams({ ref: ref.trim() })}`, "izleme-zinciri", "pdf");
export const getTrace = (ref: string) => request<TraceResponse>("GET", `/api/trace?${new URLSearchParams({ ref: ref.trim() })}`);

// ---- E7 (A8) ----
export const createReport = () => request<CreateReportResponse>("POST", "/api/reports");
export const getReports = (page = 1, pageSize = 50) => request<ReportsResponse>("GET", `/api/reports?page=${page}&pageSize=${pageSize}`);
export const getReportPdf = (id: number) => xlsxDownload(`/api/reports/${id}/pdf`, `denetim-raporu-${id}`, "pdf");

/** "Destek kodu" satırının başlangıcı; errorText ile ErrorAlert aynı ayırıcıyı kullanır. */
export const SUPPORT_PREFIX = "Destek kodu: ";

/**
 * Kullanıcıya gösterilen metin: sunucu cümlesi. İstek numarası yalnız beklenmeyen hatada (5xx ya da gövdesiz yanıt)
 * ikinci satırda "Destek kodu: <ilk 8 hane>" olarak eklenir (ui.md §10.3). Kullanıcı hatasında numara yok.
 */
export function errorText(e: unknown): string {
  if (!(e instanceof ApiFailure)) return "Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.";
  let text: string;
  if (e.body.code === "EXPORT_TOO_LARGE") {
    const n = (v: unknown) => (typeof v === "number" ? v.toLocaleString("tr-TR") : "?");
    text = `${e.body.message} Sonuç ${n(e.body.total)} satır, sınır ${n(e.body.limit)} satır.`;
  } else {
    const fields = Array.isArray(e.body.fields) ? (e.body.fields as { message: string }[]).map((f) => f.message).join(" ") : "";
    text = fields || e.body.message;
  }
  const id = String(e.body.requestId ?? "").slice(0, 8);
  const unexpected = e.status >= 500 || e.body.code === "INTERNAL_ERROR";
  return unexpected && id && id !== "-" ? `${text}
${SUPPORT_PREFIX}${id}` : text;
}

// ---- E1 (spec §6 E1) ----
export const getDashboard = () => request<DashboardResponse>("GET", "/api/dashboard");
